import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  conversationsApi,
  channelsApi,
  messagesApi,
  reactToMessage,
  type Conversation,
  type ConversationChannel,
  type SendMessagePayload
} from '@/services/api/communicationsApi';

/**
 * Hook personalizado para gestionar comunicaciones con React Query
 *
 * Features:
 * - Cache automático de conversaciones y mensajes
 * - Refetch inteligente
 * - Optimistic updates
 * - Error handling
 */

// ============================================================================
// QUERY KEYS
// ============================================================================

export const communicationKeys = {
  all: ['communications'] as const,
  conversations: () => [...communicationKeys.all, 'conversations'] as const,
  conversationsList: (filters?: Record<string, unknown>) =>
    [...communicationKeys.conversations(), 'list', { filters }] as const,
  conversationDetail: (id: string) => [...communicationKeys.conversations(), 'detail', id] as const,
  conversationMessages: (id: string) => [...communicationKeys.conversations(), id, 'messages'] as const,
  channels: () => [...communicationKeys.all, 'channels'] as const,
  channelsList: (filters?: Record<string, unknown>) =>
    [...communicationKeys.channels(), 'list', { filters }] as const,
  messages: () => [...communicationKeys.all, 'messages'] as const,
  messagesList: (filters?: Record<string, unknown>) =>
    [...communicationKeys.messages(), 'list', { filters }] as const,
};

// ============================================================================
// CONVERSATIONS HOOKS
// ============================================================================

/**
 * Hook para listar conversaciones
 */
export function useConversations(params?: {
  status?: string;
  assigned_to?: string;
  channel?: string;
  company?: string;
  project?: string;
  priority?: string;
  has_unread?: boolean;
  conversation_type?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: communicationKeys.conversationsList(params),
    queryFn: () => conversationsApi.list(params),
    staleTime: 10000,
    refetchInterval: 60000, // Safety fallback (primary updates via WebSocket)
  });
}

/**
 * Hook para obtener los canales de comunicación configurados de la empresa.
 * Solo retorna canales activos y verificados — usados para determinar
 * qué canales están realmente disponibles en el omnichannel.
 */
export function useCompanyChannels(params?: {
  channel_type?: string;
  is_active?: boolean;
  project?: string;
  company?: string;
}) {
  return useQuery({
    queryKey: communicationKeys.channelsList(params),
    queryFn: () => channelsApi.list({ is_active: true, ...params }),
    staleTime: 5 * 60 * 1000, // 5 min — channels rarely change
  });
}

/**
 * Hook para obtener una conversación específica
 */
export function useConversation(id: string) {
  return useQuery({
    queryKey: communicationKeys.conversationDetail(id),
    queryFn: () => conversationsApi.get(id),
    enabled: !!id,
    staleTime: 30000,
  });
}

/**
 * Hook para obtener mensajes de una conversación.
 * Accepts a dynamic `limit` for "load more" pagination.
 */
export function useConversationMessages(
  conversationId: string,
  params?: {
    limit?: number;
    offset?: number;
    before?: string;
    after?: string;
  }
) {
  const limit = params?.limit ?? 50;
  return useQuery({
    queryKey: [...communicationKeys.conversationMessages(conversationId), { limit }],
    queryFn: () => conversationsApi.getMessages(conversationId, { ...params, limit }),
    enabled: !!conversationId,
    staleTime: 10000,
    refetchInterval: 60000, // Safety fallback (primary updates via WebSocket)
    refetchIntervalInBackground: false,
  });
}

/**
 * Hook para enviar mensaje
 */
export function useSendMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      payload,
    }: {
      conversationId: string;
      payload: SendMessagePayload;
    }) => {
      return await conversationsApi.sendMessage(conversationId, payload);
    },
    onSuccess: (_message, variables) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationMessages(variables.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationDetail(variables.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversations(),
      });
      // Invalidate lead-level message queries (for grouped conversation view)
      queryClient.invalidateQueries({
        queryKey: [...communicationKeys.messages(), 'by-lead'],
      });
    },
  });
}

/**
 * Hook para reaccionar a un mensaje con optimistic update
 */
export function useReactToMessage() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      conversationId,
      messageId,
      emoji,
    }: {
      conversationId: string;
      messageId: string;
      emoji: string;
    }) => {
      return await reactToMessage(conversationId, messageId, emoji);
    },
    onMutate: async (variables) => {
      // Cancel outgoing refetches
      await queryClient.cancelQueries({
        queryKey: communicationKeys.conversationMessages(variables.conversationId),
      });

      // Snapshot previous data (prefix match to handle dynamic limit in key)
      const previousEntries = queryClient.getQueriesData({
        queryKey: communicationKeys.conversationMessages(variables.conversationId),
      });

      // Optimistically update conversation-level cache (prefix match)
      const reactionUpdater = (old: any) => {
        if (!old?.results) return old;
        return {
          ...old,
          results: old.results.map((msg: any) => {
            if (msg.id !== variables.messageId) return msg;
            const metadata = msg.metadata || {};
            let reactions = metadata.reactions || [];
            reactions = reactions.filter((r: any) => r.sender_type !== 'agent');
            if (variables.emoji) {
              reactions.push({
                emoji: variables.emoji,
                sender_type: 'agent',
                timestamp: new Date().toISOString(),
              });
            }
            return { ...msg, metadata: { ...metadata, reactions } };
          }),
        };
      };

      queryClient.setQueriesData(
        { queryKey: communicationKeys.conversationMessages(variables.conversationId) },
        reactionUpdater
      );

      // Also update lead-level messages cache
      queryClient.setQueriesData(
        { queryKey: [...communicationKeys.messages(), 'by-lead'] },
        reactionUpdater
      );

      return { previousEntries };
    },
    onError: (_err, _variables, context) => {
      // Roll back on error — restore all matched queries
      if (context?.previousEntries) {
        for (const [key, data] of context.previousEntries) {
          queryClient.setQueryData(key, data);
        }
      }
    },
    onSettled: (_data, _error, variables) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationMessages(variables.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: [...communicationKeys.messages(), 'by-lead'],
      });
    },
  });
}

/**
 * Hook para marcar conversación como leída
 */
export function useMarkAsRead() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (conversationId: string) => conversationsApi.markAsRead(conversationId),
    onSuccess: (_, conversationId) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationDetail(conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversations(),
      });
    },
  });
}

/**
 * Hook para asignar conversación a agente
 */
export function useAssignConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      conversationId,
      userId,
      method,
    }: {
      conversationId: string;
      userId: string;
      method?: string;
    }) => conversationsApi.assign(conversationId, userId, method),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationDetail(variables.conversationId),
      });
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversations(),
      });
    },
  });
}

/**
 * Hook para actualizar conversación
 */
export function useUpdateConversation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<Conversation> }) =>
      conversationsApi.update(id, data),
    onSuccess: (conversation) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationDetail(conversation.id),
      });
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversations(),
      });
    },
  });
}

/**
 * Hook para agregar tag
 */
export function useAddTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, tag }: { conversationId: string; tag: string }) =>
      conversationsApi.addTag(conversationId, tag),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationDetail(variables.conversationId),
      });
    },
  });
}

/**
 * Hook para quitar tag
 */
export function useRemoveTag() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ conversationId, tag }: { conversationId: string; tag: string }) =>
      conversationsApi.removeTag(conversationId, tag),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({
        queryKey: communicationKeys.conversationDetail(variables.conversationId),
      });
    },
  });
}

// ============================================================================
// MESSAGES HOOKS
// ============================================================================

/**
 * Hook para listar mensajes (general, no de una conversación específica)
 */
export function useMessages(params?: {
  conversation?: string;
  lead?: string;
  direction?: 'inbound' | 'outbound';
  status?: string;
  limit?: number;
  offset?: number;
}) {
  return useQuery({
    queryKey: communicationKeys.messagesList(params),
    queryFn: () => messagesApi.list(params),
    staleTime: 10000,
  });
}

/**
 * Hook para obtener mensajes de un lead across all conversations (grouped view).
 * Uses the by-lead endpoint with proper limit/offset pagination.
 * Returns the newest messages in chronological order.
 * Accepts a dynamic `limit` for "load more" pagination.
 */
export function useLeadMessages(leadId: string | undefined, limit = 50) {
  return useQuery({
    queryKey: [...communicationKeys.messages(), 'by-lead', leadId, { limit }],
    queryFn: () => messagesApi.byLead(leadId!, { limit, offset: 0 }),
    enabled: !!leadId,
    staleTime: 10000,
    refetchInterval: 60000, // Safety fallback (primary updates via WebSocket)
    refetchIntervalInBackground: false,
  });
}
